import React, { useState, useEffect } from 'react';
import {
  DefaultEditor, Toolbar, Separator, createButton,
  BtnUndo, BtnRedo, BtnBold, BtnItalic, BtnUnderline, BtnStrikeThrough,
  BtnNumberedList, BtnBulletList, BtnLink, BtnClearFormatting, HtmlButton, BtnStyles,
} from 'react-simple-wysiwyg';
import { AlignLeft, AlignCenter, AlignRight, AlignJustify } from 'lucide-react';
import './Articles.css';
import { DataTable } from '../ui/data-table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import FileUpload from '../ui/FileUpload';
import { articleAPI, cacheUtils } from '../../services/api';
import { articleDateInput } from '../../utils/articleDate';

const BtnAlignLeft = createButton('Зүүн талд зэрэгцүүлэх', <AlignLeft size={16} aria-hidden="true" />, 'justifyLeft');
const BtnAlignCenter = createButton('Төвд зэрэгцүүлэх', <AlignCenter size={16} aria-hidden="true" />, 'justifyCenter');
const BtnAlignRight = createButton('Баруун талд зэрэгцүүлэх', <AlignRight size={16} aria-hidden="true" />, 'justifyRight');
const BtnAlignJustify = createButton('Хоёр талд зэрэгцүүлэх', <AlignJustify size={16} aria-hidden="true" />, 'justifyFull');

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingArticleId, setEditingArticleId] = useState(null);
  const [deletingArticleId, setDeletingArticleId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    category: '',
    tag: '',
    raw_html_input: '',
    asset: '',
    published_date: articleDateInput()
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
      sortable: true,
      className: 'w-16'
    },
    {
      accessorKey: 'title',
      header: 'Title',
      sortable: true,
      className: 'min-w-48'
    },
    {
      accessorKey: 'category',
      header: 'Category',
      sortable: true,
      className: 'w-32'
    },
    {
      accessorKey: 'tag',
      header: 'Tag',
      sortable: true,
      className: 'w-32'
    },
    {
      accessorKey: 'asset',
      header: 'Image',
      sortable: false,
      className: 'w-20',
      cell: (row) => (
        row.asset ? (
          <img 
            src={row.asset} 
            alt="Article thumbnail" 
            className="w-12 h-12 object-cover rounded-md"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        ) : (
          <div className="w-12 h-12 bg-gray-100 rounded-md flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )
      )
    },
    {
      accessorKey: 'published_date',
      header: 'Published Date',
      sortable: false,
      className: 'w-40',
      cell: (row) => articleDateInput(row.published_date || row.created_at)
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      sortable: true,
      className: 'w-40',
      cell: (row) => new Date(row.created_at).toLocaleDateString()
    },
    {
      accessorKey: 'actions',
      header: 'Actions',
      sortable: false,
      className: 'w-32',
      cell: (row) => (
        <div className="flex space-x-1">
          <button
            onClick={() => handleEditArticle(row)}
            className="p-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50 rounded-full transition-colors"
            title="Edit article"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </button>
          <button
            onClick={() => handleDeleteArticle(row.id)}
            disabled={deletingArticleId === row.id}
            className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Delete article"
          >
            {deletingArticleId === row.id ? (
              <div className="animate-spin w-4 h-4 border-2 border-red-500 border-t-transparent rounded-full"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchArticles();
  }, []);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const response = await articleAPI.getAllArticles();
      setArticles(response.data || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
      setArticles([]);
      alert('Failed to fetch articles. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleEditorChange = (e) => {
    setFormData(prev => ({
      ...prev,
      raw_html_input: e.target.value
    }));
  };

  const handleImageUploadSuccess = (url, key) => {
    console.log('Image uploaded successfully:', url);
    setFormData(prev => ({
      ...prev,
      asset: url
    }));
    setUploadError(null);
  };

  const handleImageUploadError = (error) => {
    console.error('Image upload failed:', error);
    setUploadError(error);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate that content is not empty (ReactQuill returns empty content as empty string or just whitespace)
    const contentText = formData.raw_html_input.replace(/<[^>]*>/g, '').trim();
    if (!contentText) {
      alert('Please enter article content.');
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      if (isEditing) {
        const articleData = {
          ...formData,
          id: editingArticleId
        };
        
        console.log('Updating article:', articleData);
        await articleAPI.updateArticle(articleData);
        alert('Article updated successfully!');
      } else {
        console.log('Creating article:', formData);
        await articleAPI.createArticle(formData);
        alert('Article created successfully!');
      }
      
      articleAPI.clearArticleCache();
      // Refresh articles list
      await fetchArticles();
      handleCloseModal();
      
    } catch (error) {
      console.error('Error saving article:', error);
      alert(`Failed to ${isEditing ? 'update' : 'create'} article. Please try again.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditArticle = (article) => {
    setIsEditing(true);
    setEditingArticleId(article.id);
    setFormData({
      title: article.title || '',
      category: article.category || '',
      tag: article.tag || '',
      raw_html_input: article.raw_html_input || '',
      asset: article.asset || '',
      published_date: articleDateInput(article.published_date || article.created_at)
    });
    setIsModalOpen(true);
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingArticleId(articleId);
      console.log('Deleting article with ID:', articleId);
      await articleAPI.deleteArticle(articleId);
      
      // Refresh articles list after deletion
      await fetchArticles();
      
    } catch (error) {
      console.error('Error deleting article:', error);
      alert('Failed to delete article. Please try again.');
    } finally {
      setDeletingArticleId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: '',
      tag: '',
      raw_html_input: '',
      asset: '',
      published_date: articleDateInput()
    });
    setUploadError(null);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEditing(false);
    setEditingArticleId(null);
    resetForm();
  };

  return (
    <div className="max-w-7xl mx-auto p-8">
      <div className="mb-8 border-b border-gray-200 pb-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Article Management</h1>
            <p className="text-lg text-gray-600">Create and manage articles and content</p>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setIsModalOpen(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              + Add Article
            </button>
            <button
              onClick={() => {
                cacheUtils.clearArticleCache();
                fetchArticles();
              }}
              className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              disabled={loading}
            >
              🔄 Refresh
            </button>
            <button
              onClick={() => cacheUtils.clearAllCache()}
              className="px-4 py-2 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
            >
              🗑️ Clear Cache
            </button>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={articles}
        searchKey="title"
        searchPlaceholder="Search articles..."
        loading={loading}
        emptyMessage="No articles found. Create your first article!"
        pageSize={10}
      />

      {/* Create/Edit Article Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Edit Article' : 'Create New Article'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="title">Article Title *</Label>
                <Input
                  id="title"
                  name="title"
                  type="text"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter article title..."
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <Label htmlFor="published_date">Нийтлэгдсэн огноо *</Label>
                <Input
                  id="published_date"
                  name="published_date"
                  type="date"
                  value={formData.published_date}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                  aria-describedby="published-date-help"
                />
                <p id="published-date-help" className="mt-1 text-sm text-gray-500">
                  Хуучин нийтлэл хуулж оруулахдаа анх нийтлэгдсэн огноог сонгоно уу. Энэ огноо вебэд харагдана.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Category *</Label>
                  <Input
                    id="category"
                    name="category"
                    type="text"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter category..."
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <Label htmlFor="tag">Tag</Label>
                  <Input
                    id="tag"
                    name="tag"
                    type="text"
                    value={formData.tag}
                    onChange={handleInputChange}
                    placeholder="Enter tag..."
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="asset">Article Image</Label>
                <div className="mt-1">
                  <FileUpload
                    onUploadSuccess={handleImageUploadSuccess}
                    onUploadError={handleImageUploadError}
                    accept="image/*"
                    maxSize={100 * 1024 * 1024} // 100MB
                    folder="articles"
                    disabled={isSubmitting}
                    showPreview={true}
                    currentUrl={formData.asset}
                    className="w-full"
                  />
                  {uploadError && (
                    <p className="mt-1 text-sm text-red-600">
                      Upload failed: {uploadError}
                    </p>
                  )}
                  <p className="mt-1 text-sm text-gray-500">
                    Upload an image for your article (JPG, PNG, etc. - max 5MB)
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="raw_html_input">Article Content *</Label>
                <div className="mt-1">
                  <DefaultEditor
                    value={formData.raw_html_input}
                    onChange={handleEditorChange}
                    containerProps={{
                      style: {
                        minHeight: '400px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px'
                      }
                    }}
                    disabled={isSubmitting}
                    placeholder="Enter your article content here..."
                  >
                    <Toolbar>
                      <BtnUndo />
                      <BtnRedo />
                      <Separator />
                      <BtnBold />
                      <BtnItalic />
                      <BtnUnderline />
                      <BtnStrikeThrough />
                      <Separator />
                      <BtnAlignLeft aria-label="Зүүн талд зэрэгцүүлэх" disabled={isSubmitting} />
                      <BtnAlignCenter aria-label="Төвд зэрэгцүүлэх" disabled={isSubmitting} />
                      <BtnAlignRight aria-label="Баруун талд зэрэгцүүлэх" disabled={isSubmitting} />
                      <BtnAlignJustify aria-label="Хоёр талд зэрэгцүүлэх" disabled={isSubmitting} />
                      <Separator />
                      <BtnNumberedList />
                      <BtnBulletList />
                      <Separator />
                      <BtnLink />
                      <BtnClearFormatting />
                      <HtmlButton />
                      <Separator />
                      <BtnStyles />
                    </Toolbar>
                  </DefaultEditor>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Use the rich text editor to format your article content with styling, images, and links.
                  {' '}Мөр эсвэл догол мөрөө сонгоод зэрэгцүүлэх товчийг дарна уу.
                </p>
              </div>
              
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseModal}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting 
                    ? (isEditing ? 'Updating...' : 'Creating...') 
                    : (isEditing ? 'Update Article' : 'Create Article')
                  }
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Articles;
